import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Form, Card, Badge, Spinner, Table, Button } from 'react-bootstrap';
import { FiSearch, FiFilter, FiChevronLeft, FiChevronRight, FiDollarSign, FiCalendar } from 'react-icons/fi';
import moment from 'moment';

import { AppContext } from '../AppContext';
import { tre } from '../trebus';

export default function Payments() {
    const context = useContext(AppContext);
    const navigate = useNavigate();
    const [payments, setPayments] = useState({ data: [] });
    const [status, setStatus] = useState('');
    const [searchString, setSearchString] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    // -----| Load payments |-----
    const loadPayments = (page = 1) => {
        setIsLoading(true);
        tre.api('GET', `/companies/${context.idCompany}/payments`, {
            context: context,
            params: {
                page: page,
                rows: 15,
                status: status,
                str: searchString
            },
            success: (r) => {
                setPayments(r);
                setCurrentPage(page);
                setIsLoading(false);
            },
            error: () => setIsLoading(false)
        });
    };

    // Reload when filters change
    useEffect(() => {
        loadPayments(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, searchString]);

    // Navigate to payment detail
    const onSelectPayment = (payment) => {
        navigate('/payment/' + payment.id);
    };

    // Pagination
    const totalPages = payments.last_page || 1;
    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            loadPayments(page);
        }
    };

    // Month names
    const monthNames = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

    // -----| Render |-----
    return (
        <div className="container py-4">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h4 className="mb-1" style={{ color: '#202124', fontWeight: 500 }}>
                        <FiDollarSign className="me-2" style={{ color: '#1a73e8' }} />
                        Gestión de Pagos
                    </h4>
                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                        {payments.total || 0} pagos en total
                    </p>
                </div>
            </div>

            {/* Filters - Inline style */}
            <div className="d-flex flex-wrap align-items-center gap-3 mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                {/* Search */}
                <div className="position-relative" style={{ minWidth: '280px', flex: 1, maxWidth: '400px' }}>
                    <FiSearch
                        className="position-absolute"
                        style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#5f6368' }}
                    />
                    <Form.Control
                        type="text"
                        placeholder="Buscar por concepto o usuario..."
                        value={searchString}
                        onChange={(e) => setSearchString(e.target.value)}
                        style={{ paddingLeft: '38px', backgroundColor: 'white' }}
                    />
                </div>

                {/* Status filter pills */}
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted me-1" style={{ fontSize: '14px' }}>Estado:</span>
                    <Button
                        size="sm"
                        variant={status === '' ? 'primary' : 'light'}
                        onClick={() => setStatus('')}
                        style={{ borderRadius: '20px', padding: '6px 16px' }}
                    >
                        Todos
                    </Button>
                    <Button
                        size="sm"
                        variant={status === 'PAID' ? 'success' : 'light'}
                        onClick={() => setStatus('PAID')}
                        style={{ borderRadius: '20px', padding: '6px 16px' }}
                    >
                        Pagados
                    </Button>
                    <Button
                        size="sm"
                        variant={status === 'NOT_PAID' ? 'warning' : 'light'}
                        onClick={() => setStatus('NOT_PAID')}
                        style={{ borderRadius: '20px', padding: '6px 16px' }}
                    >
                        Pendientes
                    </Button>
                </div>

                {/* Clear filters - only show if filters active */}
                {(searchString || status) && (
                    <Button
                        variant="link"
                        size="sm"
                        onClick={() => { setStatus(''); setSearchString(''); }}
                        style={{ color: '#ea4335', textDecoration: 'none' }}
                    >
                        <FiFilter className="me-1" /> Limpiar
                    </Button>
                )}
            </div>

            {/* Table Card */}
            <Card>
                {/* Top Pagination Info */}
                {!isLoading && payments.data && payments.data.length > 0 && (
                    <Card.Header className="d-flex flex-wrap justify-content-between align-items-center py-3 gap-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="d-flex align-items-center gap-4">
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                <strong style={{ color: '#202124' }}>{payments.total || 0}</strong> registros
                            </span>
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                Mostrando <strong style={{ color: '#202124' }}>{payments.from || 0}</strong> - <strong style={{ color: '#202124' }}>{payments.to || 0}</strong>
                            </span>
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                Página <strong style={{ color: '#202124' }}>{currentPage}</strong> de <strong style={{ color: '#202124' }}>{totalPages}</strong>
                            </span>
                        </div>
                        {totalPages > 1 && (
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={currentPage <= 1}
                                    onClick={() => goToPage(1)}
                                >
                                    Primera
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={currentPage <= 1}
                                    onClick={() => goToPage(currentPage - 1)}
                                >
                                    <FiChevronLeft className="me-1" /> Anterior
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={currentPage >= totalPages}
                                    onClick={() => goToPage(currentPage + 1)}
                                >
                                    Siguiente <FiChevronRight className="ms-1" />
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={currentPage >= totalPages}
                                    onClick={() => goToPage(totalPages)}
                                >
                                    Última
                                </Button>
                            </div>
                        )}
                    </Card.Header>
                )}

                <Card.Body className="p-0">
                    {isLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" style={{ color: '#1a73e8' }} />
                            <p className="mt-3 text-muted">Cargando pagos...</p>
                        </div>
                    ) : payments.data && payments.data.length > 0 ? (
                        <Table hover responsive className="mb-0">
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th className="py-3 px-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>FECHA</th>
                                    <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>CONCEPTO</th>
                                    <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>USUARIO</th>
                                    <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>PERIODO</th>
                                    <th className="py-3 text-center" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>ESTADO</th>
                                    <th className="py-3 px-3 text-end" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>IMPORTE</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.data.map((payment) => (
                                    <tr
                                        key={payment.id}
                                        onClick={() => onSelectPayment(payment)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td className="py-3 px-3">
                                            <span className="text-muted" style={{ fontSize: '14px' }}>
                                                {moment(payment.created).format('DD/MM/YYYY')}
                                            </span>
                                        </td>
                                        <td className="py-3">
                                            <span style={{ color: '#202124', fontWeight: 500 }}>
                                                {payment.concept || '-'}
                                            </span>
                                        </td>
                                        <td className="py-3">
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="d-flex align-items-center justify-content-center rounded-circle me-2"
                                                    style={{
                                                        width: '32px',
                                                        height: '32px',
                                                        backgroundColor: '#e8f0fe',
                                                        color: '#1a73e8',
                                                        fontSize: '13px',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {(payment.user?.name || '?').charAt(0).toUpperCase()}
                                                </div>
                                                <span style={{ color: '#202124' }}>
                                                    {payment.user?.full_name || '-'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-3">
                                            <span className="text-muted" style={{ fontSize: '14px' }}>
                                                <FiCalendar className="me-1" />
                                                {payment.month ? `${monthNames[payment.month]} ${payment.year}` : '-'}
                                            </span>
                                        </td>
                                        <td className="py-3 text-center">
                                            <Badge
                                                bg={payment.is_paid ? 'success' : 'warning'}
                                                style={{ fontSize: '12px', padding: '6px 12px', fontWeight: 500 }}
                                            >
                                                {payment.is_paid ? 'Pagado' : 'Pendiente'}
                                            </Badge>
                                        </td>
                                        <td className="py-3 px-3 text-end">
                                            <span style={{ fontWeight: 600, color: '#202124' }}>
                                                {(Number(payment.price) || 0).toFixed(2)} €
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <div className="text-center py-5">
                            <FiDollarSign size={48} className="mb-3" style={{ color: '#dadce0' }} />
                            <p className="text-muted">No se encontraron pagos</p>
                        </div>
                    )}
                </Card.Body>

                {/* Pagination */}
                {payments.data && payments.data.length > 0 && (
                    <Card.Footer className="d-flex flex-wrap justify-content-between align-items-center py-3 gap-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="d-flex align-items-center gap-4">
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                <strong style={{ color: '#202124' }}>{payments.total || 0}</strong> registros
                            </span>
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                Mostrando <strong style={{ color: '#202124' }}>{payments.from || 0}</strong> - <strong style={{ color: '#202124' }}>{payments.to || 0}</strong>
                            </span>
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                Página <strong style={{ color: '#202124' }}>{currentPage}</strong> de <strong style={{ color: '#202124' }}>{totalPages}</strong>
                            </span>
                        </div>
                        {totalPages > 1 && (
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={currentPage <= 1}
                                    onClick={() => goToPage(1)}
                                    title="Primera página"
                                >
                                    Primera
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={currentPage <= 1}
                                    onClick={() => goToPage(currentPage - 1)}
                                >
                                    <FiChevronLeft className="me-1" /> Anterior
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={currentPage >= totalPages}
                                    onClick={() => goToPage(currentPage + 1)}
                                >
                                    Siguiente <FiChevronRight className="ms-1" />
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={currentPage >= totalPages}
                                    onClick={() => goToPage(totalPages)}
                                    title="Última página"
                                >
                                    Última
                                </Button>
                            </div>
                        )}
                    </Card.Footer>
                )}
            </Card>
        </div>
    );
}
